/**
 * Legendas das camadas de contexto: uma entrada por camada ligada no painel
 * (mais as permanentes e as com filtro de tensão), com o estilo que ela tem no
 * mapa. As classes de camadas WMS e categóricas são preenchidas depois, de forma
 * assíncrona (hydrateWmsLegends / hydrateCategoricalLegends).
 */

import { mockLayers, type Layer } from "../../../global/constants/layers";
import {
  getLayerStyle,
  hasStyleVariations,
  getStyleVariations,
} from "../../../global/layerStyles";
import {
  normalizeColorForPDF,
  normalizeFillColor,
} from "../utils/legendColorNormalizer";
import { CATEGORICAL_LEGEND_FIELDS } from "../../../global/constants/categoricalLegends";
import type { ContextLegend } from "./types";

function getVisibleVoltageValues(
  layer: Layer,
  layers: string[],
  layerCqlFilters: Record<string, (number | string)[]>
): Set<number | string> | null {
  if (!layer.voltageOptions?.length) return null;

  const filterValues = layerCqlFilters[layer.layerName] ?? [];
  if (filterValues.length > 0) return new Set(filterValues);

  const subLayerTitles = layer.subLayers?.map((s) => s.layerTitle) ?? [];
  const isParentFullyChecked = subLayerTitles.some((title) =>
    layers.includes(title)
  );
  if (isParentFullyChecked) return null;

  return new Set(layer.voltageOptions.map((o) => o.value));
}

export function generateContextLegendData(
  layers: string[],
  layerCqlFilters: Record<string, (number | string)[]>
): ContextLegend[] {
  const activeLayers = mockLayers.filter(
    (layer) =>
      layer.workspace &&
      (layer.permanent ||
        (layers && layer.title && layers.includes(layer.title)) ||
        (layerCqlFilters[layer.layerName]?.length ?? 0) > 0)
  );

  return activeLayers.map((layer) => {
    const legendItem: ContextLegend = {
      layerName: layer.title,
      category: layer.category,
      items: [],
    };

    if (layer.manualLegend) {
      legendItem.items = (
        layer.manualLegend.legendItems ?? [
          { label: layer.manualLegend.label, color: "#ffa500" },
        ]
      ).map((item) => ({
        label: item.label,
        countUnavailable: layer.manualLegend?.countUnavailable,
        style: {
          type: "line" as const,
          color: item.color,
          weight: 3,
        },
      }));
      return legendItem;
    }

    // Camada WMS pintada pelo servidor: sem estilo no front. As classes do
    // SLD são preenchidas depois, de forma assíncrona (hydrateWmsLegends).
    if (layer.type === "wms") {
      legendItem.items = [
        {
          label: layer.title,
          countUnavailable: true,
          style: { type: "polygon" as const, color: "#000000" },
        },
      ];
      return legendItem;
    }

    // Camada colorida por hash de um campo (ex.: vocação, APL): o estilo
    // depende do valor da feature, então chamar a função de estilo aqui com
    // propriedades vazias não renderiza a cor real. As classes são
    // preenchidas depois, de forma assíncrona (hydrateCategoricalLegends).
    if (layer.layerName in CATEGORICAL_LEGEND_FIELDS) {
      legendItem.items = [
        {
          label: layer.title,
          countUnavailable: true,
          style: { type: "polygon" as const, color: "#000000" },
        },
      ];
      return legendItem;
    }

    // Raster and particle layers have no vector style — show a generic raster indicator
    if (layer.type === "raster" || layer.type === "particle") {
      legendItem.items = [
        {
          label: layer.title,
          style: { type: "raster" as const },
        },
      ];
      return legendItem;
    }

    // Check if this layer has style variations
    if (hasStyleVariations(layer.layerName)) {
      const variations = getStyleVariations(layer.layerName);
      if (variations) {
        const visibleVoltages = getVisibleVoltageValues(
          layer,
          layers,
          layerCqlFilters
        );
        // Sub-layer voltages (e.g. "69 kV"/"138 kV" from LTS-neoenergia) have their own legend entry — skip the duplicate variation here.
        const subLayerTitles =
          layer.subLayers?.map((s) => s.layerTitle) ?? [];
        const visibleVariations = variations.filter((variation) => {
          if (
            subLayerTitles.some((title) => variation.label.startsWith(title))
          ) {
            return false;
          }
          return visibleVoltages
            ? visibleVoltages.has(variation.properties?.vn)
            : true;
        });

        legendItem.items = visibleVariations.map((variation) => {
          const styleFunction = getLayerStyle(layer.layerName);
          const mockFeature = { properties: variation.properties };
          const style = styleFunction ? styleFunction(mockFeature) : null;

          // Convert dashArray to string if it's an array
          let dashArray: string | undefined;
          if (style?.dashArray) {
            dashArray = Array.isArray(style.dashArray)
              ? style.dashArray.join(" ")
              : String(style.dashArray);
          }

          // Normalize colors to valid hex
          const normalizedColor = style?.color
            ? normalizeColorForPDF(String(style.color), "#333333")
            : undefined;
          const normalizedFillColor = normalizeFillColor(
            style?.fillColor ? String(style.fillColor) : undefined
          );

          const itemStyle = {
            type: (normalizedFillColor ? "polygon" : "line") as
              | "polygon"
              | "line",
            color: normalizedColor,
            fillColor: normalizedFillColor,
            fillOpacity: style?.fillOpacity,
            weight: style?.weight,
            dashArray,
          };

          return {
            label: variation.label,
            style: itemStyle,
          };
        });
      }
    } else {
      // Single style layer
      const customIcon = layer.style?.customIcon;
      const styleFunction = getLayerStyle(layer.layerName);
      const style = styleFunction ? styleFunction({}) : null;

      // Convert dashArray to string
      let dashArray: string | undefined;
      if (style?.dashArray) {
        dashArray = Array.isArray(style.dashArray)
          ? style.dashArray.join(" ")
          : String(style.dashArray);
      }

      // Normalize colors
      const normalizedColor = style?.color
        ? normalizeColorForPDF(String(style.color), "#333333")
        : undefined;
      const normalizedFillColor = normalizeFillColor(
        style?.fillColor ? String(style.fillColor) : undefined
      );

      const itemStyle = customIcon
        ? { type: "icon" as const, icon: customIcon }
        : {
            type: (normalizedFillColor ? "polygon" : "line") as
              | "polygon"
              | "line",
            color: normalizedColor,
            fillColor: normalizedFillColor,
            fillOpacity: style?.fillOpacity,
            weight: style?.weight,
            dashArray,
          };

      legendItem.items = [
        {
          label: layer.title,
          style: itemStyle,
        },
      ];
    }

    return legendItem;
  });
}
